import { useEffect, useState } from "react";
import { Button } from "../buttons/button";
import { SimpleButton } from "../buttons/simple-button";
import { useAuth } from "@/providers/auth-provider";
import { sendPokemons } from "@/services/send/send-pokemons";
import { Alert } from "@/components/alert/alert";
import { PokeMiniItem } from "../../../views/inventory-poke/poke-mini-item";
import { Dialog } from "./dialog";
import { InventoryPokemonsOutput } from "types/web-app";

interface SendPokemonDialogProps {
  isOpen: boolean;
  onClose: () => void;
  selectedPokemons: InventoryPokemonsOutput["player_pokemons"];
  onSuccess: () => void;
}

export const SendPokemonDialog = ({
  isOpen,
  onClose,
  selectedPokemons,
  onSuccess,
}: SendPokemonDialogProps) => {
  const { getAccessToken } = useAuth();
  const [receiverName, setReceiverName] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSend = async () => {
    if (!receiverName.trim()) {
      Alert("Please enter a receiver username");
      return;
    }

    if (isLoading) return;

    const accessToken = getAccessToken();
    if (!accessToken) {
      Alert("Authentication error. Please try again");
      return;
    }

    setIsLoading(true);
    try {
      const response = await sendPokemons(accessToken, {
        pokemon_ids: selectedPokemons.map((pokemon) => pokemon.id),
        receiver_name: receiverName.trim(),
      });

      if (response.ok && response.data) {
        setReceiverName("");
        Alert(
          `Successfully sent ${response.data.pokemons.length} Pokémon to ${receiverName}!`,
          () => {
            onSuccess();
          }
        );
      } else {
        Alert(response.message || "Failed to send Pokémon");
      }
    } catch (error) {
      console.error("❌ Send error:", error);
      Alert("An error occurred while sending Pokémon");
    } finally {
      setIsLoading(false);
    }
  };

  const canSend = receiverName.trim().length > 0 && !isLoading;

  return (
    <Dialog isOpen={isOpen} onClose={onClose}>
      <div className="bg-pk-garnet-3 p-6 rounded-lg border-2 border-pk-garnet-4 max-w-md w-full m-4">
        <div className="flex flex-col gap-4">
          <h2 className="text-2xl font-bold text-pk-white text-center uppercase">
            Send Pokémon
          </h2>

          {/* Receiver Input */}
          <div className="flex flex-col gap-2">
            <label className="text-pk-gray-4 text-sm font-semibold">
              RECEIVER USERNAME
            </label>
            <input
              type="text"
              value={receiverName}
              onChange={(e) => setReceiverName(e.target.value)}
              placeholder="Enter username..."
              className="w-full px-4 py-2 bg-pk-garnet-1 border-2 border-pk-garnet-2 rounded-md text-pk-gray-5 placeholder:text-pk-gray-3 outline-none focus:border-pk-garnet-4 transition"
            />
          </div>

          {/* Pokemon List */}
          <div className="flex flex-col gap-2">
            <label className="text-pk-gray-4 text-sm font-semibold">
              POKÉMONS TO SEND ({selectedPokemons.length})
            </label>
            <div className="flex flex-row gap-1 overflow-x-auto pb-2 custom-scrollbar">
              {selectedPokemons.map((pokemon) => (
                <div key={pokemon.id} className="min-w-[80px]">
                  <PokeMiniItem
                    pokemon={pokemon}
                    id={pokemon.display_id}
                    level={pokemon.level}
                    selected={true}
                    onClick={() => {}}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mt-2">
            <Button variant="night" onClick={onClose} disabled={isLoading}>
              CANCEL
            </Button>
            <Button variant="garnet" onClick={handleSend} disabled={!canSend}>
              <div className="flex items-center gap-2 justify-center">
                <span className="text-lg">
                  {isLoading ? "SENDING..." : "SEND"}
                </span>
              </div>
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
